import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { attendanceAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, formatTime, capitalizeFirst, getStatusColor, getCurrentPosition } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Clock, LogIn, LogOut, CalendarIcon, Loader2, MapPin } from "lucide-react";
import { format } from "date-fns";

const AttendancePage = () => {
  const { user } = useAuth();
  const canPunch = user?.role !== "super_admin";
  const [attendance, setAttendance] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punchLoading, setPunchLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)),
    to: new Date(),
  });

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchAttendance = async () => {
    try {
      const [historyRes, todayRes] = await Promise.all([
        attendanceAPI.getAll({
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date: format(dateRange.to, "yyyy-MM-dd"),
        }),
        attendanceAPI.getToday(),
      ]);
      setAttendance(historyRes.data);
      const t = todayRes.data;
      setTodayAttendance(t && t.id ? t : null);
    } catch (error) {
      console.error("Failed to fetch attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePunchIn = async () => {
    setPunchLoading(true);
    try {
      toast.info("Fetching your location...");
      const pos = await getCurrentPosition();
      await attendanceAPI.checkIn({ ...pos, source: "web" });
      toast.success("Checked in successfully!");
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to check in");
    } finally {
      setPunchLoading(false);
    }
  };

  const handlePunchOut = async () => {
    setPunchLoading(true);
    try {
      let pos = {};
      try {
        pos = await getCurrentPosition();
      } catch (e) {
        // location optional for check-out
      }
      await attendanceAPI.checkOut({ ...pos, source: "web" });
      toast.success("Checked out successfully!");
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to check out");
    } finally {
      setPunchLoading(false);
    }
  };

  const isCheckedIn = todayAttendance?.check_in_time && !todayAttendance?.check_out_time;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Attendance</h1>
          <p className="text-slate-500">Track your attendance and working hours</p>
        </div>
      </div>

      {/* Today's Attendance Card */}
      {canPunch && (
        <Card className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-white/80 text-sm flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> Today&apos;s Attendance (location verified)
                </p>
                <p className="text-2xl font-bold mt-1">{format(new Date(), "EEEE, MMMM d, yyyy")}</p>
                {todayAttendance && (
                  <div className="flex items-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <LogIn className="w-4 h-4 text-white/70" />
                      <span>{formatTime(todayAttendance.check_in_time)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <LogOut className="w-4 h-4 text-white/70" />
                      <span>{todayAttendance.check_out_time ? formatTime(todayAttendance.check_out_time) : "--:--"}</span>
                    </div>
                    {todayAttendance.working_hours != null && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-white/70" />
                        <span>{todayAttendance.working_hours.toFixed(2)} hrs</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                {!todayAttendance ? (
                  <Button
                    className="bg-white text-indigo-600 hover:bg-white/90 h-14 px-8 text-lg"
                    onClick={handlePunchIn}
                    disabled={punchLoading}
                    data-testid="punch-in-btn"
                  >
                    {punchLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <LogIn className="w-5 h-5 mr-2" />
                        Check In
                      </>
                    )}
                  </Button>
                ) : isCheckedIn ? (
                  <Button
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 h-14 px-8 text-lg"
                    onClick={handlePunchOut}
                    disabled={punchLoading}
                    data-testid="punch-out-btn"
                  >
                    {punchLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <LogOut className="w-5 h-5 mr-2" />
                        Check Out
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="text-center px-6 py-3 bg-white/10 rounded-lg">
                    <p className="font-medium">Day Complete</p>
                    <p className="text-sm text-white/70">
                      {todayAttendance.working_hours?.toFixed(1) || 0} hours worked
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date Range Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">Filter by date:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal" data-testid="attendance-date-filter">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => range && setDateRange(range)}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Attendance History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Attendance History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Check In</TableHead>
                <TableHead>Check Out</TableHead>
                <TableHead>Working Hours</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No attendance records found
                  </TableCell>
                </TableRow>
              ) : (
                attendance.map((record) => (
                  <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                    <TableCell className="font-medium">{formatDate(record.date)}</TableCell>
                    <TableCell>{record.check_in_time ? formatTime(record.check_in_time) : "-"}</TableCell>
                    <TableCell>{record.check_out_time ? formatTime(record.check_out_time) : "-"}</TableCell>
                    <TableCell>
                      {record.working_hours != null ? `${record.working_hours.toFixed(2)} hrs` : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge className={`badge badge-${getStatusColor(record.status)}`}>
                        {capitalizeFirst((record.status || "").replace(/_/g, " "))}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendancePage;
